import type { AdminVehicleCatalogMakeListItemDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';

import { formatDate } from '../../../utils/format';
import {
  CatalogFlagChips,
  CatalogSourceLabel,
  CatalogStatusChip,
  MakeInitials,
} from '../_components/CatalogChips';

/**
 * Columns of the makes grid (AUT-437), after `vehicle-catalog.html`:
 * Марка / Моделей / Флаги / Статус / Источник / Изменено.
 *
 * Differs from the mockup on purpose: no alias line under the name and no
 * «· N скрыто» (the list item carries neither), «Изменено» is the date only
 * (the API has no editor name). `modelCount` includes hidden models, so the
 * «нет моделей» chip shows only at 0 — the drawer tells the full story.
 */
type Col = GridColDef<AdminVehicleCatalogMakeListItemDto>;

const MakeCell = ({ row }: { row: AdminVehicleCatalogMakeListItemDto }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%', minWidth: 0 }}>
    <MakeInitials name={row.name} />
    <Typography
      variant="body2"
      fontWeight={600}
      noWrap
      sx={{ opacity: row.status === 'hidden' ? 0.6 : 1 }}
    >
      {row.name}
    </Typography>
  </Box>
);

const ModelCountCell = ({ count, t }: { count: number; t: TFunction }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
    <Typography variant="body2" fontFamily="monospace">
      {count}
    </Typography>
    {count === 0 ? (
      <Chip size="small" color="warning" variant="outlined" label={t('vehicleCatalog.noModels')} />
    ) : null}
  </Box>
);

const centred = { display: 'flex', alignItems: 'center', height: '100%' } as const;

export const buildCatalogColumns = (t: TFunction): Col[] => [
  {
    field: 'name',
    headerName: t('vehicleCatalog.columns.make'),
    flex: 1.6,
    minWidth: 220,
    sortable: false,
    renderCell: ({ row }) => <MakeCell row={row} />,
  },
  {
    field: 'modelCount',
    headerName: t('vehicleCatalog.columns.models'),
    width: 170,
    sortable: false,
    renderCell: ({ row }) => <ModelCountCell count={row.modelCount} t={t} />,
  },
  {
    field: 'flags',
    headerName: t('vehicleCatalog.columns.flags'),
    flex: 1,
    minWidth: 200,
    sortable: false,
    renderCell: ({ row }) => (
      <Box sx={centred}>
        <CatalogFlagChips popular={row.popular} onSale={row.onSale} />
      </Box>
    ),
  },
  {
    field: 'status',
    headerName: t('vehicleCatalog.columns.status'),
    width: 120,
    sortable: false,
    renderCell: ({ row }) => (
      <Box sx={centred}>
        <CatalogStatusChip status={row.status} />
      </Box>
    ),
  },
  {
    field: 'source',
    headerName: t('vehicleCatalog.columns.source'),
    width: 130,
    sortable: false,
    renderCell: ({ row }) => (
      <Box sx={centred}>
        <CatalogSourceLabel source={row.source} />
      </Box>
    ),
  },
  {
    field: 'updatedAt',
    headerName: t('vehicleCatalog.columns.updatedAt'),
    width: 130,
    sortable: false,
    renderCell: ({ row }) => (
      <Box sx={centred}>
        <Typography variant="body2" fontFamily="monospace">
          {formatDate(row.updatedAt)}
        </Typography>
      </Box>
    ),
  },
];
